// The Revenue Budget Review's reading of a Cost Plus Forecast row — shared
// by the Overview section (voltaGridRevenueReview.js) and its Excel export
// (voltaGridRevenueReviewXlsx.js).
import { COST_TYPE_DESCRIPTION } from './procurementCategories';

export const TOL = 0.02;

export const CT_DESC = {
    '51100': 'Regular Time', '51200': 'Overtime', '51300': 'Burden', '51350': 'Ops Support', '51400': 'BYOD', '51500': 'Per Diem',
    '51600': 'Truck Pay', '51700': 'Welding Rig Pay', '51800': 'Field Bonus', '52100': 'Inside Equipment Rental',
    ...COST_TYPE_DESCRIPTION,
};

/**
 * Where a row stands. costVar / billVar are each EAC less budget as a share
 * of that budget (null with no budget or while the billable side is still
 * pricing). The word is the status; the tone only repeats it.
 */
export const reviewStatus = ({ curCostBudget, costEac, curBillBudget, billEac } = {}) => {
    const costVar = curCostBudget > 0 && costEac != null ? (costEac - curCostBudget) / curCostBudget : null;
    const billVar = curBillBudget > 0 && billEac != null ? (billEac - curBillBudget) / curBillBudget : null;
    if (costVar == null) return { word: curCostBudget > 0 ? 'Pricing…' : 'No budget', tone: 'neutral', costVar, billVar };
    const cost = costVar > TOL ? 'over' : costVar < -TOL ? 'under' : 'on';
    const bill = billVar == null ? null : billVar < -TOL ? 'short' : billVar > TOL ? 'ahead' : 'on';
    let word;
    let tone;
    if (cost === 'on') {
        if (bill === 'short') { word = 'On cost, billing short'; tone = 'caution'; }
        else if (bill === 'ahead') { word = 'On cost, billing ahead'; tone = 'good'; }
        else { word = 'On budget'; tone = 'good'; }
    } else if (cost === 'over') {
        if (bill === 'short') { word = 'Over cost, billing short'; tone = 'bad'; }
        else if (bill === 'ahead') { word = 'Over cost, billing ahead'; tone = 'caution'; }
        else { word = 'Over cost'; tone = 'bad'; }
    } else {
        if (bill === 'short') { word = 'Under cost, billing short'; tone = 'info'; }
        else if (bill === 'ahead') { word = 'Under cost, billing ahead'; tone = 'good'; }
        else { word = 'Under cost'; tone = 'good'; }
    }
    return { word, tone, costVar, billVar };
};

/**
 * The review's rows off a costPlusModel: each class with its cost types
 * (and its "no cost type" remainder) under it, the contingency when it has
 * anything, Non-Billable, and the total. `jtdBill` is the Cost Plus tab's
 * billable to date (sent + the rest priced); the EACs are null until the
 * plan and the pricing are in, so a half-loaded page never shows $0.
 */
export const reviewRows = (model, { ready = false, planReady = false } = {}) => {
    const shape = (r) => ({
        ...r,
        jtdBillShown: ready ? (r.sentBill || 0) + (r.unsentBill || 0) : null,
        costEacShown: planReady ? r.costEac : null,
        billEacShown: ready && planReady ? r.billEac : null,
    });
    const rows = (model.classes || []).map((c) => ({ ...shape(c), kind: 'class', subs: (c.subRows || []).concat(c.noCt ? [c.noCt] : []).map(shape) }));
    if (model.contingency && (model.contingency.costBudget || model.contingency.etc)) rows.push({ ...shape(model.contingency), kind: 'class', subs: [] });
    if (model.nonBillable && model.nonBillable.jtdCost) rows.push({ ...shape(model.nonBillable), kind: 'nb', subs: [] });
    return { rows, total: shape(model.total || {}) };
};

export const statusOf = (r) => (r.kind === 'nb'
    ? { word: 'Not billable', tone: 'neutral', costVar: null, billVar: null }
    : reviewStatus({ curCostBudget: r.curCostBudget, costEac: r.costEacShown, curBillBudget: r.curBillBudget, billEac: r.billEacShown }));

export const subLabel = (s) => (s.ct ? `${s.ct} ${CT_DESC[s.ct] || ''}`.trim() : (s.label || 'No cost type'));
